import chinaCalibration from './china-calibration.json';
import worldCalibration from './world-calibration.json';

// device_info.location: WGS84. Bare pairs are explicitly longitude,latitude;
// ambiguous latitude-first pairs are never silently swapped.
export function parseLocation(value) {
  let location = value;
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return null;
    try { location = JSON.parse(text); } catch {
      const match = text.match(/^\s*([+-]?\d+(?:\.\d+)?)\s*[,，]\s*([+-]?\d+(?:\.\d+)?)\s*$/);
      if (!match) return null;
      location = [match[1], match[2]];
    }
  }
  const pair = Array.isArray(location) ? location : location && typeof location === 'object'
    ? [location.lng ?? location.lon ?? location.longitude, location.lat ?? location.latitude] : [];
  if (pair.length !== 2 || pair.some(v => v === null || v === undefined || typeof v === 'boolean' || String(v).trim() === '')) return null;
  const [lng, lat] = pair.map(Number);
  return Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90 ? { lng, lat } : null;
}

export function projectChina({ lng, lat }) {
  const [n, c, scale, rotation, tx, ty] = chinaCalibration.parameters;
  const radians = Math.PI / 180;
  const rho = Math.sqrt(c - 2 * n * Math.sin(lat * radians)) / n;
  const theta = n * (lng - 105) * radians + rotation;
  return [tx + scale * rho * Math.sin(theta), ty + scale * rho * Math.cos(theta)];
}

export function projectWorld({ lng, lat }) {
  const u = (((lng - 150 + 180) % 360 + 360) % 360 - 180) / 180;
  const v = lat / 90;
  return worldCalibration.powers.reduce((xy, [i, j], k) => {
    const factor = u ** i * v ** j;
    return xy.map((coordinate, axis) => coordinate + factor * worldCalibration.coefficients[k][axis]);
  }, [0, 0]);
}

// Only the lookup tessellates curves; the visible/hit/lift artwork always uses
// the unchanged official SVG paths. Cache per asset rather than per station.
const indexes = new WeakMap();
function flattenPath(d) {
  const tokens = d.match(/[MLCZmlcz]|[-+]?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g) || [];
  const rings = []; let ring = []; let point = [0, 0]; let i = 0;
  while (i < tokens.length) {
    const command = tokens[i++];
    if (command === 'M' || command === 'L') {
      point = [Number(tokens[i++]), Number(tokens[i++])];
      if (command === 'M') { if (ring.length) rings.push(ring); ring = []; }
      ring.push(point);
    } else if (command === 'C') {
      const a = [Number(tokens[i++]), Number(tokens[i++])];
      const b = [Number(tokens[i++]), Number(tokens[i++])];
      const end = [Number(tokens[i++]), Number(tokens[i++])];
      for (let step = 1; step <= 12; step++) {
        const t = step / 12; const s = 1 - t;
        ring.push([
          s ** 3 * point[0] + 3 * s * s * t * a[0] + 3 * s * t * t * b[0] + t ** 3 * end[0],
          s ** 3 * point[1] + 3 * s * s * t * a[1] + 3 * s * t * t * b[1] + t ** 3 * end[1],
        ]);
      }
      point = end;
    } else if (command === 'Z' || command === 'z') {
      point = ring[0];
    } else { throw new Error('Unsupported official map path command'); }
  }
  if (ring.length) rings.push(ring);
  return rings;
}
function getIndex(data) {
  if (!indexes.has(data)) {
    const items = data.regions.flatMap(region => region.paths.map(path => {
      const rings = flattenPath(path.d);
      const bounds = rings.flat().reduce((b, p) => [Math.min(b[0], p[0]), Math.min(b[1], p[1]), Math.max(b[2], p[0]), Math.max(b[3], p[1])], [Infinity, Infinity, -Infinity, -Infinity]);
      return { region, rings, bounds, area: (bounds[2] - bounds[0]) * (bounds[3] - bounds[1]) };
    })).sort((a, b) => a.area - b.area);
    indexes.set(data, items);
  }
  return indexes.get(data);
}
function insideRing([x, y], ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[i]; const [bx, by] = ring[j];
    if ((ay > y) !== (by > y) && x < (bx - ax) * (y - ay) / (by - ay) + ax) inside = !inside;
  }
  return inside;
}
export function findRegion(data, point) {
  if (!data || !point.every(Number.isFinite)) return null;
  const symbol = data.regions.find(r => r.point && Math.hypot(point[0] - r.point[0], point[1] - r.point[1]) <= 2.5);
  if (symbol) return symbol.code;
  for (const { region, rings, bounds: b } of getIndex(data)) {
    if (point[0] < b[0] || point[1] < b[1] || point[0] > b[2] || point[1] > b[3]) continue;
    if (rings.reduce((inside, ring) => inside !== insideRing(point, ring), false)) return region.code;
  }
  return null;
}

export function locateSites(rows, china, world) {
  const sites = []; let invalid = 0;
  for (const row of rows) {
    const coordinates = parseLocation(row.location);
    if (!coordinates) { invalid++; continue; }
    const chinaPoint = projectChina(coordinates);
    const province = findRegion(china, chinaPoint);
    const worldPoint = projectWorld(coordinates);
    // Province evidence takes precedence, including Taiwan, Hong Kong and Macau.
    const country = province ? 'CN' : world ? findRegion(world, worldPoint) : null;
    sites.push({ id: row.id, name: row.station_name || row.sn || String(row.id), sn: row.sn,
      demo: row.model === 'MAP_DEMO' && row.vendor === 'EMS_DEMO', coordinates,
      hasActiveAlarm: row.has_active_alarm == null ? null : Number(row.has_active_alarm) === 1,
      capacity: row.capacity == null || row.capacity === '' ? null : Number(row.capacity),
      power: row.power == null || row.power === '' ? null : Number(row.power),
      chinaPoint, worldPoint, province, country });
  }
  return { sites, invalid };
}

export async function loadAllSites(fetchPage) {
  const rows = new Map(); const pageSize = 200; let page = 1;
  while (true) {
    const result = await fetchPage({ page, pageSize });
    if (!result?.success || !Array.isArray(result.data) || !Number.isFinite(Number(result.total))) throw new Error('Site list unavailable');
    const before = rows.size;
    for (const row of result.data) rows.set(row.id, row);
    if (rows.size >= Number(result.total)) return [...rows.values()];
    if (result.data.length === 0 || rows.size === before) throw new Error('Incomplete site list');
    page++;
  }
}
